if (!window.customElements.get('search-resource-tabs')) {
  class SearchResourceTabs extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.mountWhenReady();
        return;
      }

      this.initialized = true;
      this.section = this.closest('.shopify-section--apps');
      this.tabs = Array.from(this.querySelectorAll('[data-search-resource-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-search-resource-panel]'));
      this.rootUrl = this.dataset.rootUrl || '/';
      this.loadSequence = 0;
      this.inputTimer = null;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(tab.dataset.searchResourceTab));
        tab.addEventListener('keydown', (event) => this.handleTabKeydown(event));
      });

      this.mountWhenReady();
    }

    disconnectedCallback() {
      this.pageObserver?.disconnect();
      this.inputObserver?.disconnect();
      window.clearTimeout(this.inputTimer);
    }

    mountWhenReady() {
      const mount = () => {
        const page = this.section?.querySelector('.ais-page');
        const inputArea = page?.querySelector('.ais-input');

        if (!page || !inputArea) return false;

        if (this.parentElement !== page || this.previousElementSibling !== inputArea) {
          inputArea.insertAdjacentElement('afterend', this);
          return true;
        }

        this.page = page;
        this.productElements = Array.from(page.querySelectorAll('.ais-facets-button, .ais-facets, .ais-block, .ais-pagination-container'));
        this.productPanel = page.querySelector('.ais-block');

        if (this.productPanel) {
          this.productPanel.id = this.tabs.find((tab) => tab.dataset.searchResourceTab === 'products')?.getAttribute('aria-controls') || '';
          this.productPanel.setAttribute('role', 'tabpanel');
          this.productPanel.setAttribute('aria-labelledby', this.tabs.find((tab) => tab.dataset.searchResourceTab === 'products')?.id || '');
        }

        this.hidden = false;
        this.bindSearchInputWhenReady();
        this.load(this.currentQuery());
        return true;
      };

      if (mount()) return;

      this.pageObserver = new MutationObserver(() => {
        if (mount()) this.pageObserver.disconnect();
      });
      this.pageObserver.observe(this.section || document.documentElement, { childList: true, subtree: true });
    }

    bindSearchInputWhenReady() {
      if (this.bindSearchInput()) return;

      this.inputObserver = new MutationObserver(() => {
        if (this.bindSearchInput()) this.inputObserver.disconnect();
      });
      this.inputObserver.observe(this.page, { childList: true, subtree: true });
    }

    bindSearchInput() {
      const input = this.page?.querySelector('.ais-SearchBox-input');
      if (!input) return false;
      if (input.dataset.searchResourceTabsBound === 'true') return true;

      input.dataset.searchResourceTabsBound = 'true';
      input.addEventListener('input', () => {
        window.clearTimeout(this.inputTimer);
        this.inputTimer = window.setTimeout(() => this.load(input.value.trim()), 350);
      });

      return true;
    }

    currentQuery() {
      return this.page?.querySelector('.ais-SearchBox-input')?.value.trim()
        || new URL(window.location.href).searchParams.get('q')?.trim()
        || '';
    }

    async load(query) {
      const sequence = ++this.loadSequence;
      this.setBusy(true);

      const [productCount, resources] = await Promise.all([
        this.fetchProductCount(query),
        this.fetchContentResources(query)
      ]);

      if (sequence !== this.loadSequence) return;

      const blogs = resources.articles || [];
      const collections = resources.collections || [];
       const pages = resources.pages || [];
      const counts = {
        products: productCount,
        blogs: blogs.length,
         collections: collections.length,
        pages: pages.length
      };

      this.renderPanel('blogs', blogs, 'Blog');
      this.renderPanel('collections', collections, 'Collection');
       this.renderPanel('pages', pages, 'Page');
      this.updateTabs(counts);

       const firstAvailable = ['products', 'blogs', 'collections', 'pages'].find((type) => counts[type] === null || counts[type] > 0) || 'products';
      this.activate(firstAvailable);
      this.setBusy(false);
    }

    async fetchContentResources(query) {
       if (!query) return { articles: [], collections: [], pages: [] };

      try {
        const url = new URL(`${this.rootUrl}search/suggest.json`, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'article,collection,page');
        url.searchParams.set('resources[limit]', '10');
        url.searchParams.set('resources[limit_scope]', 'each');

        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Shopify content search failed.');

        const data = await response.json();
        return data?.resources?.results || { articles: [], collections: [], pages: [] };
      } catch (error) {
       return { articles: [], collections: [], pages: [] };
      }
    }

    async fetchProductCount(query) {
      if (!query) return 0;

      try {
        const config = await this.waitForAlgoliaConfig();
        const appId = String(config?.app_id || '');
        const apiKey = String(config?.search_api_key || '');
        const indexPrefix = String(config?.index_prefix || '');

        if (!/^[a-z0-9]+$/i.test(appId) || !apiKey || !/^[a-z0-9_-]+$/i.test(indexPrefix)) return this.detectProductCount();

        const response = await fetch(`https://${appId}-dsn.algolia.net/1/indexes/${encodeURIComponent(`${indexPrefix}products`)}/query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Algolia-API-Key': apiKey,
            'X-Algolia-Application-Id': appId
          },
          body: JSON.stringify({ query, hitsPerPage: 0, distinct: true, filters: window.VPA?.b2bSearchFilters || '' })
        });

        if (!response.ok) return this.detectProductCount();
        const data = await response.json();
        return Number.isFinite(data?.nbHits) ? data.nbHits : this.detectProductCount();
      } catch (error) {
        return this.detectProductCount();
      }
    }

    async detectProductCount() {
      for (let attempt = 0; attempt < 20; attempt += 1) {
        const stats = this.page?.querySelector('.ais-Stats');
        const statsText = stats?.textContent || '';
        const statsNumber = statsText.match(/out of\s+([\d,]+)/iu)?.[1]
          || statsText.match(/([\d,]+)\s+results?\s+found/iu)?.[1];

        if (statsNumber) return Number(statsNumber.replaceAll(',', ''));
        if (this.page?.querySelector('.ais-hit-empty')) return 0;

        const visibleHits = this.page?.querySelectorAll('.ais-hit:not(.ais-hit-empty), .ais-Hits-item').length || 0;
        if (visibleHits > 0) return null;

        await new Promise((resolve) => window.setTimeout(resolve, 100));
      }

      return null;
    }

    async waitForAlgoliaConfig() {
      for (let attempt = 0; attempt < 30; attempt += 1) {
        if (window.algoliaShopify?.config) return window.algoliaShopify.config;
        await new Promise((resolve) => window.setTimeout(resolve, 100));
      }

      return null;
    }

    updateTabs(counts) {
      this.tabs.forEach((tab) => {
        const type = tab.dataset.searchResourceTab;
        const count = counts[type];
        const countElement = tab.querySelector(`[data-search-resource-count="${type}"]`);

        tab.disabled = count === 0;
        tab.setAttribute('aria-disabled', String(count === 0));
        if (countElement) countElement.textContent = Number.isFinite(count) ? `(${count})` : '';
      });
    }

    setBusy(busy) {
      this.setAttribute('aria-busy', String(busy));
      this.tabs.forEach((tab) => {
        if (busy) tab.setAttribute('data-loading', 'true');
        else tab.removeAttribute('data-loading');
      });
    }

    activate(type) {
      const activeTab = this.tabs.find((tab) => tab.dataset.searchResourceTab === type && !tab.disabled);
      if (!activeTab) return;

      this.tabs.forEach((tab) => {
        const active = tab === activeTab;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      const showProducts = type === 'products';
      this.productElements?.forEach((element) => {
        element.hidden = !showProducts;
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.searchResourcePanel !== type;
      });
    }

    handleTabKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      const enabledTabs = this.tabs.filter((tab) => !tab.disabled);
      const currentIndex = enabledTabs.indexOf(event.currentTarget);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + enabledTabs.length) % enabledTabs.length;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % enabledTabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = enabledTabs.length - 1;

      event.preventDefault();
      enabledTabs[nextIndex]?.focus();
      enabledTabs[nextIndex]?.click();
    }

    renderPanel(type, items, typeLabel) {
      const panel = this.panels.find((item) => item.dataset.searchResourcePanel === type);
      if (!panel) return;

      panel.replaceChildren();

      if (items.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'search-resource-tabs__empty';
        empty.textContent = `No ${type.toLowerCase()} found.`;
        panel.append(empty);
        return;
      }

      const count = document.createElement('p');
      count.className = 'search-resource-tabs__count';
      count.textContent = `${items.length} ${typeLabel.toLowerCase()} result${items.length === 1 ? '' : 's'}`;

      const grid = document.createElement('div');
      grid.className = `search-resource-tabs__grid search-resource-tabs__grid--${type}`;

      items.forEach((item) => grid.append(this.createCard(item, typeLabel)));
      panel.append(count, grid);
    }

    createCard(item, typeLabel) {
      const card = document.createElement('a');
      const media = document.createElement('span');
      const content = document.createElement('span');
      const type = document.createElement('span');
      const title = document.createElement('span');
      const safeTitle = String(item?.title || 'Untitled');
      const imageUrl = this.imageUrl(item);

      card.className = 'search-resource-card';
      card.href = this.safeUrl(item?.url);
      media.className = 'search-resource-card__media';
      content.className = 'v-stack gap-1';
      type.className = 'search-resource-card__type';
      type.textContent = typeLabel;
      title.className = 'search-resource-card__title';
      title.textContent = safeTitle;

      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = safeTitle;
        image.loading = 'lazy';
        image.decoding = 'async';
        media.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'search-resource-card__placeholder';
        placeholder.textContent = safeTitle.charAt(0).toUpperCase();
        media.append(placeholder);
      }

      content.append(type, title);
      card.append(media, content);
      return card;
    }

    imageUrl(item) {
      const image = item?.image || item?.featured_image;
      if (typeof image === 'string') return image;
      return String(image?.url || image?.src || '');
    }

    safeUrl(value) {
      try {
        const url = new URL(String(value || '#'), window.location.origin);
        if (url.origin !== window.location.origin) return '#';
        return `${url.pathname}${url.search}${url.hash}`;
      } catch (error) {
        return '#';
      }
    }
  }

  window.customElements.define('search-resource-tabs', SearchResourceTabs);
}
